<script setup lang="ts">
import type { Chat as ChatType } from '@ai-sdk/vue'
import type { ChatStatus, ToolUIPart, UIMessage, UITools } from 'ai'

import { Chat } from '@ai-sdk/vue'
import { FocusScope } from 'reka-ui'
import { DefaultChatTransport } from 'ai'
import LineStop from '@docue/docue-ui-v2/icons/LineStop.vue'
import LineXClose from '@docue/docue-ui-v2/icons/LineXClose.vue'
import LineSend03 from '@docue/docue-ui-v2/icons/LineSend03.vue'
import LineChevronLeft from '@docue/docue-ui-v2/icons/LineChevronLeft.vue'
import LineAlertCircle from '@docue/docue-ui-v2/icons/LineAlertCircle.vue'
import DTFieldInput from '@docue/docue-ui-v2/ui-components/DTField/DTFieldInput.vue'
import LineMessagePlusSquare from '@docue/docue-ui-v2/icons/LineMessagePlusSquare.vue'

import { mapToApiLanguage } from '~/utils/api/language'
import ChatLoader from '~/components/Chat/ChatLoader.vue'

import type { CommandPaletteTab } from '../AppCommandPalette.vue'

type MessageDataStatus = 'validation' | 'invalid-response'

const props = defineProps<{
  query?: string
  isNew: boolean
}>()

const emit = defineEmits<{
  tabChange: [tab: CommandPaletteTab]
}>()

const START_MESSAGE_ID = 'finding-assistant-start'
const PROCESSING_STATUSES: ChatStatus[] = ['streaming', 'submitted']

const { t } = useI18n()
const { country } = useAuth()
const { chatId, messages: storedMessages, store } = useFindingAssistantChatHistory()
const { $posthog } = useNuxtApp()
const config = useRuntimeConfig()
const { language } = useLanguage()
const { notifyBugsnag } = useReportError()

const startChatMessage = computed<UIMessage>(() => ({
  role: 'assistant',
  id: START_MESSAGE_ID,
  parts: [{ type: 'text', text: t('appCommandPalette.chat.startMessage') }],
}))

const requestError = ref<Error>()
const chat = shallowRef<ChatType<UIMessage>>()
const messages = computed(() => chat.value?.messages ?? [])
const status = computed(() => chat.value?.status)
const messageDataStatus = ref<MessageDataStatus | undefined>()
watch(messageDataStatus, (status, _, onCleanup) => {
  if (!status)
    return

  const timeoutId = setTimeout(() => {
    messageDataStatus.value = undefined
  }, 2500)

  onCleanup(() => clearTimeout(timeoutId))
})

const isProcessing = computed(() => PROCESSING_STATUSES.includes((status.value ?? '') as ChatStatus))

const lastMessage = computed(() => messages.value[messages.value.length - 1])
const lastAssistantMessageState = computed(() => {
  if (isProcessing.value && lastMessage.value?.role !== 'assistant')
    return t('appCommandPalette.chat.generation.start')

  if (!lastMessage.value || lastMessage.value.role !== 'assistant' || !isProcessing.value)
    return undefined

  const textPart = lastMessage.value.parts?.find(part => part.type === 'text')
  if (textPart && textPart.state !== 'done')
    return t('appCommandPalette.chat.generation.responding')

  const templateSearchPart = lastMessage.value.parts?.find(part => part.type === 'tool-templateSearch')
  if (templateSearchPart && !(templateSearchPart as ToolUIPart<UITools>).state.startsWith('output-'))
    return t('appCommandPalette.chat.generation.searchingTemplates')

  const hubSearchPart = lastMessage.value.parts?.find(part => part.type === 'tool-hubSearch')
  if (hubSearchPart && !(hubSearchPart as ToolUIPart<UITools>).state.startsWith('output-'))
    return t('appCommandPalette.chat.generation.searchingArticles')

  const reasoningPart = lastMessage.value.parts?.find(part => part.type === 'reasoning')
  if (reasoningPart && reasoningPart.state !== 'done')
    return t('appCommandPalette.chat.generation.thinking')

  if (messageDataStatus.value) {
    return messageDataStatus.value === 'validation'
      ? t('appCommandPalette.chat.generation.validating')
      : t('appCommandPalette.chat.generation.reAnalysing')
  }

  return t('appCommandPalette.chat.generation.processing')
})

const didLastAssistantMessageFailResponseGeneration = computed(() => {
  if (!lastMessage.value || lastMessage.value.role !== 'assistant' || isProcessing.value)
    return false

  return !lastMessage.value.parts?.some(part => part.type === 'text' && !!part.text.trim().length)
})

const isMessageFailure = computed(() => !isProcessing.value && (didLastAssistantMessageFailResponseGeneration.value || !!requestError.value))
const isMessageStatusVisible = computed(() => isProcessing.value || isMessageFailure.value)

const chatInitedWithMessageId = ref<string | undefined>()

const messagesWithFeedbackShown = ref<Set<string>>(new Set())

const isFeedbackInputsVisible = computed(() => {
  // do not show if just one message (initial intro message)
  if (messages.value.length < 2)
    return false

  const lastMsg = unref(lastMessage)
  if (!lastMsg || lastMsg.role !== 'assistant')
    return false

  if (messagesWithFeedbackShown.value.has(lastMsg.id))
    return false

  return !isProcessing.value && !isMessageFailure.value
})

defineExpose({
  isProcessing,
})

watch(messages, (newMessages) => {
  store(chatId.value, newMessages)
}, { deep: true })

const inputValue = ref('')
const inputRef = useTemplateRef<InstanceType<typeof DTFieldInput>>('inputRef')

const submit = () => {
  if (!inputValue.value.trim() || isProcessing.value || !chat.value)
    return

  requestError.value = undefined

  chat.value.sendMessage({
    role: 'user',
    parts: [{ type: 'text', text: inputValue.value.trim() }],
  })
  $posthog.capture('chat_message_sent')

  inputValue.value = ''
}

const sendExampleMessage = (message: string) => {
  if (!chat.value || isProcessing.value) {
    return
  }

  chat.value.sendMessage({
    role: 'user',
    parts: [{ type: 'text', text: message }],
  })
  $posthog.capture('chat_example_message_sent')
}

const initChat = (messages: UIMessage[] = [], targetChatId: string) => {
  requestError.value = undefined
  $posthog.capture('chat_initiated', { is_new: !messages.length || (messages.length === 1 && messages[0]?.id === START_MESSAGE_ID) })

  const messagesToStore = messages.length ? messages : [unref(startChatMessage)]
  store(targetChatId, messagesToStore)
  chatInitedWithMessageId.value = messagesToStore[messagesToStore.length - 1]?.id

  chat.value = new Chat({
    messages: messagesToStore,
    transport: new DefaultChatTransport({
      api: `${config.public.templateAssistantUrl}/api/query`,
      headers: {
        'Accept-Language': mapToApiLanguage(language.value),
        Country: country.value ?? '',
      },
      body: {
        pSId: $posthog.get_session_id(),
        pDId: $posthog.get_distinct_id(),
        pTId: targetChatId,
      },
    }),
    onError(error) {
      console.error(error)
      notifyBugsnag(error)
      requestError.value = error
    },
    onData(dataPart) {
      if (dataPart.type !== 'data-status') {
        return
      }

      switch (dataPart.data) {
        case 'validation':
          messageDataStatus.value = 'validation'
          break
        case 'invalid-response':
          messageDataStatus.value = 'invalid-response'
          break
      }
    },
  })
}

onMounted(() => {
  const args: [messages: UIMessage[], chatId: string] = props.isNew
    ? [
        props.query ? [] : [unref(startChatMessage)],
        crypto.randomUUID(),
      ]
    : [
        storedMessages.value,
        chatId.value,
      ]

  initChat(...args)

  if (props.query && props.isNew) {
    chat.value?.sendMessage({
      role: 'user',
      parts: [{ type: 'text', text: props.query }],
    })
  }

  nextTick(() => {
    inputRef.value?.$el.focus()
  })
})

const stopChat = () => {
  if (chat.value?.error)
    chat.value.clearError()

  chat.value?.stop()
}

const cleanupIncompleteMessage = ({ shouldReInitChat = false } = {}) => {
  const localMessages = [...messages.value]
  const lastMessage = localMessages[localMessages.length - 1]
  if (!lastMessage || lastMessage?.role === 'user')
    return

  if (lastMessage.id === START_MESSAGE_ID)
    return void store(crypto.randomUUID(), [])

  const hasValidTextPart = lastMessage.parts?.some(part => part.type === 'text' && !!part.text.trim().length)
  if (hasValidTextPart)
    return

  localMessages.pop()

  if (shouldReInitChat)
    initChat(localMessages, chatId.value)
  else
    store(chatId.value, localMessages)
}

onBeforeUnmount(() => {
  stopChat()
  cleanupIncompleteMessage()
})

const handleStartNewConversation = () => {
  if (isProcessing.value)
    return

  initChat([unref(startChatMessage)], crypto.randomUUID())
}

const exampleMessages = computed(() => {
  if (!country.value)
    return []

  const messagesByCountry: Record<App.Internals.Enums.Country, string[]> = {
    FI: [
      t('appCommandPalette.chat.examples.FI.example-1'),
      t('appCommandPalette.chat.examples.FI.example-2'),
      t('appCommandPalette.chat.examples.FI.example-3'),
      t('appCommandPalette.chat.examples.FI.example-4'),
    ],
    GB: [
      t('appCommandPalette.chat.examples.GB.example-1'),
      t('appCommandPalette.chat.examples.GB.example-2'),
      t('appCommandPalette.chat.examples.GB.example-3'),
      t('appCommandPalette.chat.examples.GB.example-4'),
    ],
    SE: [
      t('appCommandPalette.chat.examples.SE.example-1'),
      t('appCommandPalette.chat.examples.SE.example-2'),
      t('appCommandPalette.chat.examples.SE.example-3'),
      t('appCommandPalette.chat.examples.SE.example-4'),
    ],
    DE: [
      t('appCommandPalette.chat.examples.DE.example-1'),
      t('appCommandPalette.chat.examples.DE.example-2'),
      t('appCommandPalette.chat.examples.DE.example-3'),
      t('appCommandPalette.chat.examples.DE.example-4'),
    ],
    PL: [
      t('appCommandPalette.chat.examples.PL.example-1'),
      t('appCommandPalette.chat.examples.PL.example-2'),
      t('appCommandPalette.chat.examples.PL.example-3'),
      t('appCommandPalette.chat.examples.PL.example-4'),
    ],
  }

  return messagesByCountry[country.value].filter(message => !!message.trim().length)
})

const isExampleMessagesVisible = computed(() =>
  !isProcessing.value && lastMessage.value?.id === startChatMessage.value.id && !!exampleMessages.value.length,
)
</script>

<template>
  <FocusScope
    trapped
    loop
    as-child
  >
    <div class="flex flex-col">
      <div class="flex w-full items-center justify-between gap-2 border-b border-b-gray-200 p-2 pr-3.5">
        <button
          class="group flex items-center gap-2 text-sm font-medium"
          @click="() => (void emit('tabChange', 'search'))"
        >
          <span class="flex size-8 items-center justify-center rounded-full group-hover:bg-gray-100">
            <LineChevronLeft class="size-4 shrink-0 text-gray-600" />
          </span>
          <span>{{ t('appCommandPalette.chat.title') }}</span>
        </button>
        <div class="flex items-center gap-2">
          <DTTooltip :content="t('appCommandPalette.chat.startNewConversation')">
            <DTButton
              size="sm"
              class="!p-2"
              :disabled="isProcessing"
              @click="handleStartNewConversation"
            >
              <span class="sr-only">{{ t('appCommandPalette.chat.startNewConversation') }}</span>
              <LineMessagePlusSquare class="size-4 shrink-0 text-gray-600" />
            </DTButton>
          </DTTooltip>
        </div>
      </div>

      <Suspense>
        <ChatContainer
          :key="chatId"
          :messages
          :status
          :request-error
          :chat-id
          @message-regenerate-requested="() => {
            requestError = undefined
            chat?.regenerate()
          }"
        >
          <template
            v-if="isMessageStatusVisible || isFeedbackInputsVisible"
            #bottom
          >
            <div
              v-if="isFeedbackInputsVisible"
              class="pointer-events-auto flex items-center divide-x divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white"
            >
              <div class="flex items-center gap-2 py-2 pl-3.5 pr-2">
                <span class="text-sm">{{ t('appCommandPalette.chat.feedbackQuestion') }}</span>
                <ChatMessageFeedbackPopover
                  :message-id="lastMessage?.id"
                  :chat-id
                />
              </div>
              <button
                class="flex size-10 items-center justify-center hover:bg-gray-100"
                @click="messagesWithFeedbackShown.add(lastMessage!.id)"
              >
                <LineXClose class="size-4 shrink-0 text-gray-600" />
              </button>
            </div>
            <div
              v-else
              class="pointer-events-auto flex flex-col items-center gap-2 rounded-lg border border-gray-200 bg-white px-3.5 py-2"
            >
              <div class="flex items-center gap-2">
                <component
                  :is="isMessageFailure ? LineAlertCircle : ChatLoader"
                  class="size-4 text-red"
                />
                <span class="text-sm font-medium">
                  {{ isMessageFailure ? t('appCommandPalette.chat.responseGenerationFailed') : `${lastAssistantMessageState}…` }}
                </span>
              </div>
              <DTButton
                v-if="isMessageFailure"
                variant="link"
                dense
                size="xs"
                @click="() => {
                  requestError = undefined
                  chat?.regenerate()
                }"
              >
                {{ capitalize(t('retry')) }}
              </DTButton>
            </div>
          </template>
        </ChatContainer>

        <template #fallback>
          <div class="flex h-20 w-full items-center justify-center">
            <DTLoaderDots />
          </div>
        </template>
      </Suspense>

      <div
        v-if="isExampleMessagesVisible"
        class="flex flex-col gap-1.5 overflow-hidden px-3.5"
      >
        <UiButton
          v-for="(message, index) in exampleMessages"
          :key="message"
          size="2xs"
          variant="outline"
          class="max-w-max origin-top will-change-transform animate-in fade-in-0 slide-in-from-top-full fill-mode-backwards direction-normal"
          :class="{
            'delay-100': index === 1,
            'delay-200': index === 2,
            'delay-300': index === 3,
          }"
          type="button"
          @click="sendExampleMessage(message)"
        >
          {{ message }}
        </UiButton>
      </div>

      <form
        class="p-3.5"
        @submit.prevent="submit"
      >
        <DTFieldInputWrapper size="sm">
          <DTFieldInput
            ref="inputRef"
            v-model="inputValue"
            :placeholder="t('appCommandPalette.chat.messagePlaceholder')"
            name="message"
          />
          <DTFieldInputInlineAddon>
            <DTIconButton
              v-if="!isProcessing"
              type="submit"
              variant="plain"
            >
              <LineSend03 class="size-5 shrink-0 text-gray-800" />
            </DTIconButton>
            <DTIconButton
              v-else
              variant="plain"
              @click="() => {
                stopChat()
                cleanupIncompleteMessage({ shouldReInitChat: true })
              }"
            >
              <LineStop class="size-5 shrink-0 text-gray-800" />
            </DTIconButton>
          </DTFieldInputInlineAddon>
        </DTFieldInputWrapper>
      </form>
    </div>
  </FocusScope>
</template>
